/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import { vyaparApiBaseUrl } from '@/root/api/apiConstants'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import { toTitleCase } from '@/root/utils/common/strings/title-case-string'
import {
  fetchPOSTRequest,
  fetchRequest,
} from '@/root/utils/network-adapter/fetch-request'
import Modal from '@/stories/components/Common/Modal'
import Cookies from 'js-cookie'
import { useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import ConvertToDeliveryStatus from './ConvertToDeliveryStatus'
import openCenteredPopup from '@/root/utils/payments/openCenteredPopup'
// import { useRouter } from 'next/router'

export interface TConvertPositionsGet {
  user_id?: string
  request_id?: string
  ind_stock_id?: string
  exchange?: string
  txn_type?: string
  isin?: string
  max_convertible_qty?: number
  acc_balance?: number
  mar_percentage?: number
  intraday_position_data?: IntradayPositionData
  stock_detail?: StockDetail
  market_data?: MarketData
}

export interface IntradayPositionData {
  Qty?: number
  AvgPrice?: number
  exchange?: string
  Description?: string
  LongDescription?: string
  RealisedGains?: number
  GrossQty?: number
}

export interface MarketData {
  date?: Date
  market_closed?: boolean
  market_status_txt?: string
  source?: string
  market_status_message?: string
}

export interface StockDetail {
  security_id?: string
  segment?: string
  name?: string
  icon_url?: string
  live_price?: number
  price_change?: number
  price_updated_at?: string
  lower_circuit?: number
  upper_circuit?: number
  tick_size?: number
  max_qty?: number
  max_qty_intraday?: number
  max_qty_exchange?: number
  min_qty?: number
  lot_size?: number
  sm_series?: string
  bz_series?: boolean
  t_2_t_settlement_type?: boolean
}

export interface TConvertToDeliveryPOST {
  status?: string
  data?: Data
}

export interface Data {
  id?: number
  navlink?: Navlink
  meta?: null
  message?: string
  should_proceed_forward?: boolean
}

export interface Navlink {
  android?: string
  ios?: string
  web?: string
}

export interface TConvertToDeliveryPOSTBody {
  data?: CtDData
  payload?: Payload
}

export interface CtDData {
  ind_stock_id?: string
  txn_type?: string
  product_to?: string
  product_from?: string
  exchange?: string
  isin?: string
  security_id?: string
}

export interface Payload {
  quantity?: string
}

export default function ConvertToDeliveryModal({
  showModal,
  onClose,
  ind_key,
}: {
  showModal: boolean
  onClose: () => void
  ind_key?: string
}) {
  const [quantity, setQuantity] = useState('')
  const token = Cookies.get('token')

  // const router = useRouter()

  const {
    mutate: convertToDelivery,
    isLoading: mutationIsLoading,
    isSuccess: mutationIsSuccess,
    data: mutationData,
  } = useMutation<TConvertToDeliveryPOST, unknown, TConvertToDeliveryPOSTBody>(
    'convert-to-delivery-post',
    config => {
      return fetchPOSTRequest({
        method: 'POST',
        url: `${vyaparApiBaseUrl}/api/v1/mis/positions/convert?response_format=rest`,
        headers: { Authorization: `Bearer ${token}`, Platform: 'web' },
        data: config,
      })
    },
    {
      onSuccess(data) {
        if (data.data?.navlink?.web?.includes('tpinflow')) {
          openCenteredPopup(
            data.data.navlink.web,
            'tpinWindow',
            window,
            700,
            700,
          )
        }
      },
    },
  )

  const { data, isLoading } = useQuery<TConvertPositionsGet>(
    'convert-to-delivery',
    () => {
      const token = Cookies.get('token')
      return fetchRequest({
        url: `${vyaparApiBaseUrl}/api/v1/mis/positions/convert?ind_stock_id=${ind_key}&response_format=rest`,
        config: {
          headers: { Authorization: `Bearer ${token}`, Platform: 'web' },
        },
      })
    },
    {
      onError() {
        void onClose()
      },
    },
  )

  const showAddMoney =
    (data?.acc_balance ?? 0) <
      0.8 * Number(quantity) * (data?.intraday_position_data?.AvgPrice ?? 1) &&
    (data?.intraday_position_data?.Qty ?? 0) > 0

  const dontShowAddMoneyDebitPrice =
    0.8 * Number(quantity) * (data?.intraday_position_data?.AvgPrice ?? 1)

  const quantityInvalidated =
    Number(quantity) > (data?.max_convertible_qty ?? 0)

  const showCDSLmessaging =
    data?.txn_type === 'sell' && (data.max_convertible_qty ?? 0) > 0

  const showDematHoldingsMessaging = data?.max_convertible_qty === 0

  if (mutationIsLoading || isLoading) {
    return (
      <Modal showModal={showModal} bodyClassName="max-w-[400px]">
        <div className="p-5">
          <SpinnerLoader />
        </div>
      </Modal>
    )
  } else if (
    mutationIsSuccess &&
    !mutationData.data?.navlink?.web?.includes('tpinflow')
  ) {
    return (
      <ConvertToDeliveryStatus
        id={mutationData.data?.id}
        showModal={showModal}
        onClose={onClose}
      />
    )
  }

  return (
    <Modal
      showModal={showModal}
      onClose={onClose}
      bodyClassName="max-w-[400px]"
    >
      <div className="p-4">
        <p className="text-lg">
          Convert {toTitleCase(data?.txn_type ?? '')} Order Position
        </p>
        <p className="text-brand-grey-base text-sm">
          Convert your intraday position to delivery
        </p>
        <div className="text-brand-grey-dark mt-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-base font-medium">Enter Quantity</span>

            <input
              className="border-brand-grey-light text-brand-grey-dark hide-number-controls w-1/4 border-0 border-b-2 py-1 text-center text-lg outline-none ring-0 placeholder:text-center placeholder-shown:text-center focus:border-0 focus:border-b-2 focus:placeholder-transparent focus:outline-0 focus:ring-0"
              placeholder="0"
              type="number"
              min={0}
              value={quantity}
              onKeyDown={evt =>
                (evt.key === 'e' || evt.key === '-' || evt.key === '.') &&
                evt.preventDefault()
              }
              onChange={e => {
                setQuantity(e.target.value)
              }}
            />
          </div>
          {quantityInvalidated && (
            <p className="text-brand-red-cinnabar  px-2 py-2 text-center  text-xs ">
              {' '}
              Max {data?.max_convertible_qty} qty can be converted to delivery.
            </p>
          )}
        </div>
        <div className="mt-10 px-3 text-center">
          {showCDSLmessaging && (
            <div className="bg-brand-tertiary-orange  text-brand-grey-base rounded-lg px-2 py-2 text-center text-xs">
              {' '}
              Your existing holdings or investment (delivery) positions of{' '}
              {data.max_convertible_qty} qty will be sold. You will need to
              authorize holdings on CDSL, if not already done
            </div>
          )}
          {showDematHoldingsMessaging && (
            <div className="bg-brand-tertiary-orange  text-brand-grey-base rounded-lg px-2 py-2 text-center text-xs">
              {' '}
              Demat holdings are required to convert open sell positions. You
              don't have any holdings required for conversion.
            </div>
          )}
          {!showAddMoney &&
            !quantityInvalidated &&
            Number(quantity) !== 0 &&
            !showCDSLmessaging && (
              <div className="bg-brand-tertiary-orange  text-brand-grey-base rounded-lg px-2 py-2 text-center text-xs">
                {' '}
                Additional balance of approx{' '}
                {formatNumberToINRCommaSeparatedString(
                  dontShowAddMoneyDebitPrice,
                )}{' '}
                will be debited from your wallet for conversion
              </div>
            )}
          {showAddMoney && (
            <div className="bg-brand-tertiary-orange  text-brand-grey-base rounded-lg px-2 py-2 text-center text-xs">
              Add additional money to wallet or enter a lower quantity to
              convert your position to delivery
            </div>
          )}
          <p className="my-2 text-sm">
            Wallet Balance:{' '}
            <span className="font-medium">
              {formatNumberToINRCommaSeparatedString(data?.acc_balance ?? 0)}
            </span>
          </p>

          {showAddMoney ? (
            <button
              className="bg-brand-green-dark text-brand-white w-full rounded-full py-2"
              onClick={() => {
                window.location.href = '/stocks/add-money'
              }}
            >
              Add Money
            </button>
          ) : (
            <button
              className="bg-brand-green-dark text-brand-white w-full rounded-full py-2 disabled:opacity-50"
              disabled={
                Number(quantity) > (data?.max_convertible_qty ?? 0) ||
                Number(quantity) === 0
              }
              onClick={() => {
                convertToDelivery({
                  data: {
                    ind_stock_id: ind_key,
                    exchange: '',
                    isin: data?.isin,
                    product_from: 'INTRADAY',
                    product_to: 'CNC',
                    security_id: data?.stock_detail?.security_id,
                    txn_type: data?.txn_type,
                  },
                  payload: { quantity },
                })
              }}
            >
              Convert Position
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
